import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import type {
  CrewBitConfig,
  ConfigProfile,
  ConfigService,
  ConfigInvoiceSettings,
  ConfigBookingPage,
  ConfigFinancialSettings,
  ConfigLanguage,
  ConfigAISettings,
} from '../../../types/configExport';

export interface ExportSectionDef {
  key: string;
  label: string;
}

export const EXPORT_SECTION_DEFS: ExportSectionDef[] = [
  { key: 'profile_hours', label: 'Business profile & hours' },
  { key: 'services', label: 'Services & pricing' },
  { key: 'invoice', label: 'Invoice & quote branding' },
  { key: 'booking', label: 'Booking page config' },
  { key: 'financial', label: 'Financial settings' },
  { key: 'marketing', label: 'Notification & marketing rules' },
  { key: 'ai', label: 'AI feature settings' },
  { key: 'language', label: 'Language & communication' },
];

function sanitize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function useConfigExport() {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);

  const [sections, setSections] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const def of EXPORT_SECTION_DEFS) init[def.key] = true;
    return init;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const toggleSection = useCallback((key: string) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const buildConfig = useCallback(async (): Promise<string> => {
    if (!businessId || !business) throw new Error('No business');

    const config: CrewBitConfig = {
      _version: '1.0',
      _exportedAt: new Date().toISOString(),
      _exportedFrom: business.name,
      _industry: business.industry ?? 'other',
    };

    // Profile & hours
    if (sections.profile_hours) {
      const profile: ConfigProfile = {};
      if (business.name) profile.name = business.name;
      if (business.address) profile.address = business.address;
      if (business.city) profile.city = business.city;
      if (business.province) profile.province = business.province;
      if (business.postal_code) profile.postal_code = business.postal_code;
      if (business.phone) profile.phone = business.phone;
      if (business.email) profile.email = business.email;
      if (business.timezone) profile.timezone = business.timezone;
      config.profile = profile;

      const { data: hoursData } = await supabase
        .from('business_hours')
        .select('day_of_week, is_open, open_time, close_time')
        .eq('business_id', businessId)
        .order('day_of_week');
      if (hoursData && hoursData.length > 0) {
        config.hours = { days: hoursData };
      }
    }

    // Services
    if (sections.services) {
      const { data: svcData } = await supabase
        .from('services')
        .select('name, description, duration_minutes, price, category, enable_dynamic_pricing, min_price, max_price, intro_enabled, intro_price, intro_label')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('sort_order')
        .order('name');
      if (svcData && svcData.length > 0) {
        config.services = svcData as ConfigService[];
      }
    }

    // Invoice settings — brand-only export per
    // §SESSION_INVOICE_SETTINGS_LEGACY_RETIREMENT D17. Tax / payment terms
    // / accept_credit_card live elsewhere (BFS canonical RPC + Stripe
    // gating server-side); excluding them keeps export shape aligned with
    // the brand-only writer surface.
    if (sections.invoice) {
      // §Settings Phase 2B: accept_etransfer column retired — exports carry
      // accepted_methods (the source of truth) instead.
      const { data: invData } = await supabase
        .from('invoice_settings')
        .select('logo_url, accent_color, invoice_prefix, default_note, default_thank_you, accepted_methods, etransfer_email')
        .eq('business_id', businessId)
        .maybeSingle();
      if (invData) {
        config.invoiceSettings = invData as ConfigInvoiceSettings;
      }
    }

    // Booking page
    if (sections.booking) {
      const { data: bpData } = await supabase
        .from('booking_pages')
        .select('headline, description, font_mood, show_prices, show_operators, show_testimonials, operator_selection_mode, allow_walk_ins, cancellation_policy')
        .eq('business_id', businessId)
        .maybeSingle();
      if (bpData) {
        config.bookingPage = bpData as ConfigBookingPage;
      }
    }

    // Financial settings
    if (sections.financial) {
      const { data: finData } = await supabase
        .from('business_financial_settings')
        .select('hst_rate, hst_number, default_payment_terms, default_deposit_percentage, tips_enabled, operator_rent_enabled')
        .eq('business_id', businessId)
        .maybeSingle();
      if (finData) {
        config.financialSettings = finData as ConfigFinancialSettings;
      }
    }

    // Marketing automations
    if (sections.marketing) {
      const { data: mktData } = await supabase
        .from('marketing_automations')
        .select('type, enabled, days_threshold, subject, body, include_booking_link, include_intro_offer')
        .eq('business_id', businessId);
      if (mktData && mktData.length > 0) {
        config.marketing = { automations: mktData };
      }
    }

    // AI settings
    if (sections.ai) {
      const { data: aiData } = await supabase
        .from('business_ai_settings')
        .select('ai_enabled, description_cleanup, message_suggestions, quote_scope_writer, receipt_scanning, expense_categorization, photo_estimates, photo_reports, change_order_analysis, daily_summary')
        .eq('business_id', businessId)
        .maybeSingle();
      if (aiData) {
        config.aiSettings = aiData as ConfigAISettings;
      }
    }

    // Language
    if (sections.language) {
      const lang: ConfigLanguage = {};
      if (business.client_language) lang.client_language = business.client_language;
      if (business.auto_translate_messages != null) lang.auto_translate_messages = business.auto_translate_messages;
      if (Object.keys(lang).length > 0) config.language = lang;
    }

    return JSON.stringify(config, null, 2);
  }, [businessId, business, sections]);

  const exportConfig = useCallback(async () => {
    if (!business) return;
    setLoading(true);
    setError(null);
    try {
      const json = await buildConfig();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `crewbit-config-${sanitize(business.name)}-${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
      try {
        localStorage.setItem('crewbit-last-export', new Date().toISOString());
      } catch { /* non-critical */ }
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [business, buildConfig]);

  const copyToClipboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await buildConfig();
      await navigator.clipboard.writeText(json);
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Copy failed');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [buildConfig]);

  return { sections, toggleSection, exportConfig, copyToClipboard, loading, error };
}
